// ============================================================================
// Biometric Gate — Validasi ketersediaan biometrik 512-dim subjek
// Ref: Google Docs 'Metodologi Skor Kepercayaan' Section 3.1
// ============================================================================
// Tanpa biometrik, akurasi identifikasi tidak cukup untuk klaim HIGH CONFIDENCE.
// Sistem prioritas transparansi over false confidence — kalau biometric gagal
// load, scoring di-cap di 60 (max MEDIUM tier) dan operator dapat warning visible.
// ============================================================================

/**
 * Check apakah subjek punya biometric embeddings tersedia + valid.
 * Return score cap (100 atau 60) + warning message.
 *
 * @param {Array<Array<number>>} subjectEmbeddings - Array of 512-dim vectors (1-5 pose)
 * @returns {{available: boolean, scoreCap: number, warning: string|null, embeddingCount: number}}
 */
export function checkBiometricGate(subjectEmbeddings) {
  // Validasi input — harus array non-empty
  if (!subjectEmbeddings || !Array.isArray(subjectEmbeddings) || subjectEmbeddings.length === 0) {
    return {
      available: false,
      scoreCap: 60,
      warning: 'Biometrik 512-dim tidak tersedia — skor maksimum dikunci di 60',
      embeddingCount: 0,
    };
  }

  // Validasi setiap embedding adalah 512-dim numeric vector
  const validEmbeddings = subjectEmbeddings.filter(
    emb => Array.isArray(emb) && emb.length === 512 && emb.every(v => typeof v === 'number' && !isNaN(v))
  );

  if (validEmbeddings.length === 0) {
    return {
      available: false,
      scoreCap: 60,
      warning: 'Biometrik 512-dim gagal validasi — skor maksimum dikunci di 60',
      embeddingCount: 0,
    };
  }

  // Min 1 pose required, ideal 5 pose untuk best accuracy
  return {
    available: true,
    scoreCap: 100,
    warning: null,
    embeddingCount: validEmbeddings.length,
  };
}

/**
 * Variant untuk frontend — cek dari faceMatchSubject state object.
 * faceMatchSubject di-populate dari backend SSE event face_match.subject_ready.
 *
 * @param {{ready: boolean, embedding_count: number, dim: number} | null} faceMatchSubject
 * @returns {{available: boolean, scoreCap: number, warning: string|null, embeddingCount: number}}
 */
export function checkBiometricGateFromState(faceMatchSubject) {
  if (!faceMatchSubject || !faceMatchSubject.ready) {
    return {
      available: false,
      scoreCap: 60,
      warning: 'Biometrik 512-dim belum ready — skor maksimum dikunci di 60',
      embeddingCount: 0,
    };
  }
  const count = faceMatchSubject.embedding_count || 0;
  if (count === 0) {
    return {
      available: false,
      scoreCap: 60,
      warning: 'Biometrik 512-dim tidak terkunci — skor maksimum dikunci di 60',
      embeddingCount: 0,
    };
  }
  // Optional: warning kalau dim tidak 512 (model mismatch)
  if (faceMatchSubject.dim && faceMatchSubject.dim !== 512) {
    return {
      available: false,
      scoreCap: 60,
      warning: `Biometrik dimensi ${faceMatchSubject.dim} ≠ 512 — model mismatch, skor dikunci di 60`,
      embeddingCount: count,
    };
  }
  return {
    available: true,
    scoreCap: 100,
    warning: null,
    embeddingCount: count,
  };
}
